// El panel del dueno.
//
// Antes mostraba el MRR, que es el valor de los planes vigentes. Ese numero
// se mueve unos pocos soles por dia y no distingue plata que entro este mes
// de plata que entro en agosto: por eso el panel se veia congelado. Ahora lo
// primero es la venta real del mes, dia por dia, contra el mes anterior.
//
// El orden de la pagina es el orden en que se decide: cuanto entro, quien se
// va a ir, que hay que repartir hoy, y recien al final de que esta hecho el
// negocio.

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';

import LayoutPages from './../../components/LayoutPages/LayoutPages';
import SaludoCeo from '../../sections/dashboard/SaludoCeo';
import KpiCard from '../../sections/dashboard/KpiCard';
import MixCard from '../../sections/dashboard/MixCard';
import Atencion from '../../sections/dashboard/Atencion';
import VentasHero from '../../sections/dashboard/VentasHero';
import PulsoOperacion from '../../sections/dashboard/PulsoOperacion';
import OlaRenovaciones from '../../sections/dashboard/OlaRenovaciones';
import useCeoMetrics from '../../sections/dashboard/useCeoMetrics';
import { Aparece } from '../../sections/dashboard/motion';
import './Dashboard.scss';
import { soles, numero, nombreMes } from '../../sections/dashboard/formato';
import AvisoRetraso from '../../sections/entregas/AvisoRetraso';
import NoEntregadas from '../../sections/entregas/NoEntregadas';
import AvanceMotorizados from '../../sections/entregas/AvanceMotorizados';

const DashboardPage = () => {
  const { datos, cargando, error } = useCeoMetrics();

  return (
    <LayoutPages cssClass="dashCeo">
      {/* El saludo necesita las cifras del dia, asi que mientras cargan va un
          encabezado sobrio en su lugar y no un bloque vacio dando saltos. */}
      {datos
        ? <Box sx={{ mb: 2.5 }}><Aparece><SaludoCeo datos={datos} /></Aparece></Box>
        : <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Resumen</Typography>
            <Typography variant="h2">Panel de control</Typography>
          </Box>
      }

      {/* Si hoy el reparto va tarde, se avisa desde aqui y aparece al
          instante en la app de todos los clientes del dia. */}
      <Box sx={{ mb: 2.5 }}><AvisoRetraso /></Box>

      {error &&
        <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
      }

      {cargando &&
        <Grid container spacing={2.5}>
          <Grid size={12}><Skeleton variant="rounded" height={330} sx={{ borderRadius: 2 }} /></Grid>
          {[0, 1, 2, 3].map((i) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={i}>
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      }

      {datos &&
        <Grid container spacing={2.5}>
          <Grid size={12}>
            <Aparece i={0}><VentasHero datos={datos} /></Aparece>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Aparece i={1}>
            <KpiCard
              title="Clientes activos"
              valorNumerico={datos.clientes.activos}
              formato={(v) => numero(Math.round(v))}
              caption={`+${datos.clientes.nuevosMes} nuevos y ${datos.clientes.bajasMes} bajas en ${nombreMes(datos.hoy)}`}
              icon={<GroupsRoundedIcon />}
              color="primary"
              series={datos.serieActivos.map((p) => p.clientesActivos)}
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {/* De los que ya vencieron este mes, cuantos volvieron. Los que
                todavia no vencen no entran: incluirlos hundiria el numero a
                principio de mes sin que nadie se haya ido. */}
            <Aparece i={2}>
            <KpiCard
              title="Renuevan"
              value="—"
              valorNumerico={datos.clientes.tasaRenovacion === null ? undefined : datos.clientes.tasaRenovacion}
              formato={(v) => `${Math.round(v)}%`}
              caption={`${datos.clientes.renovaronMes} volvieron · ${datos.clientes.bajasMes} no`}
              icon={<AutorenewRoundedIcon />}
              color="secondary"
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Aparece i={3}>
            <KpiCard
              title="Ticket promedio"
              valorNumerico={datos.mes.ticketPromedio}
              formato={soles}
              caption={`por venta de ${nombreMes(datos.hoy)}`}
              icon={<ReceiptLongRoundedIcon />}
              color="info"
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {/* Comida ya cobrada que todavia hay que servir. No es caja: es
                deuda con el cliente, y es el piso de produccion de las
                proximas semanas. */}
            <Aparece i={4}>
            <KpiCard
              title="Comida por servir"
              valorNumerico={datos.operacion.valorEnviosPendientes}
              formato={soles}
              caption={`${numero(datos.operacion.enviosPendientes)} envíos ya pagados`}
              icon={<RestaurantRoundedIcon />}
              color="warning"
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            <Aparece i={5}><OlaRenovaciones semanas={datos.olaRenovaciones} /></Aparece>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Aparece i={6}>
              <Atencion
                clientes={datos.clientes}
                primeraSemana={datos.olaRenovaciones[0]}
                porRenovar={datos.porRenovar}
                hoy={datos.hoy}
              />
            </Aparece>
          </Grid>

          <Grid size={12}>
            {/* Lo que necesita accion hoy. Las dos tarjetas se esconden solas
                cuando no hay nada que mirar. */}
            <NoEntregadas />
            <AvanceMotorizados />
          </Grid>

          <Grid size={12}>
            <Aparece i={7}><PulsoOperacion operacion={datos.operacion} /></Aparece>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Aparece i={8}>
            <MixCard
              titulo="De qué está hecho el negocio"
              subtitulo={`Clientes activos y lo que vendió cada plan en ${nombreMes(datos.hoy)}`}
              items={datos.porPlan}
              conMonto
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Aparece i={9}>
            <MixCard
              titulo="Dónde están los clientes"
              subtitulo="Distritos con más entregas activas"
              items={datos.porDistrito}
              color="primary"
            />
            </Aparece>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Aparece i={10}>
            {/* El objetivo se carga al completar el perfil. Hoy casi nadie lo
                tiene, y eso es el dato: son clientes pagando de los que no
                sabemos nada para recomendarles ni para venderles de nuevo. */}
            <MixCard
              titulo="Para qué vienen"
              subtitulo={(() => {
                const sinPerfil = datos.porObjetivo.find((o) => o.etiqueta === 'SIN_PERFIL');
                return sinPerfil
                  ? `${numero(sinPerfil.clientes)} de ${numero(datos.clientes.activos)} nunca completaron su perfil`
                  : 'Objetivo que eligieron al registrarse';
              })()}
              items={datos.porObjetivo}
              color="info"
            />
            </Aparece>
          </Grid>
        </Grid>
      }
    </LayoutPages>
  );
};

export default DashboardPage;
